export interface DocumentsDetails {
  proofOfAddressIds: ReadonlyArray<string>;
  proofOfDeathIds: ReadonlyArray<string>;
  willAndProbateIds: ReadonlyArray<string>;
  grantOfRepresentationIds: ReadonlyArray<string>;
  appliedForGrantOfRepresentationOn?: string;
  tags?: ReadonlyArray<string>;
  proofOfBankAccountIds?: ReadonlyArray<string>;
  deathCertificateIds?: ReadonlyArray<string>;
}

export type UploadedDocument = {
  id: string;
  filename: string;
};
export enum InternalFileTags {
  NotifierGoa = "notifier_goa",
  KycReport = "kyc_report",
  SpPortal = "sp_portal",
  BackOffice = "backoffice",
  Chat = "chat",
}
export enum FileTags {
  ProofOfAddress = "proof_of_address",
  ProofOfDeath = "proof_of_death",
  Will = "will",
  GrantOfRepresentation = "grant_of_representation",
  NokId = "nok_id",
  ExecutorId = "executor_id",
  ExecutorGoa = "executor_goa",
  NokGoa = "nok_goa",
  ProofOfBankAccount = "proof_of_bank_account",
  GasMeterReading = "gas_meter_reading",
  ElectricityMeterReading = "electricity_meter_reading",
}

export const fileTagsToUiInfo = {
  [FileTags.ProofOfAddress]: "Proof of Address",
  [FileTags.ProofOfDeath]: "Proof Of Death",
  [FileTags.Will]: "Will and Probate",
  [FileTags.GrantOfRepresentation]: "Grant of Representation",
  [FileTags.NokId]: "ID Document for next of kin",
  [FileTags.ExecutorId]: "ID Document for Executor of the Will",
  [FileTags.NokGoa]: "Grant of Authority for next of kin",
  [FileTags.ExecutorGoa]: "Grant of Authority for Executor of the Will",
  [FileTags.ProofOfBankAccount]: "Proof Of Bank Account",
  [FileTags.GasMeterReading]: "Gas Meter Reading",
  [FileTags.ElectricityMeterReading]: "Electricity Meter Reading",
};

export interface Document {
  readonly id: string;
  readonly downloadUrl: string;
  readonly filename: string;
  readonly uploadedAt?: Date;
  readonly updatedAt?: Date;
  readonly tags: Array<InternalFileTags | FileTags>;
}

export type DocumentShell = {
  id: string;
  name: string;
  tags?: ReadonlyArray<InternalFileTags | FileTags>;
};

export type UploadsRecord = {
  readonly originalIds: ReadonlyArray<string>;
  readonly currentIds: ReadonlyArray<string>;
  readonly previousDocuments: ReadonlyArray<Document>;
  readonly files: ReadonlyArray<File>;
};

export type DocumentsPersistedState = {
  readonly record: DocumentsRecord;
  readonly errors: DocumentErrors;
  readonly hasChanges: boolean;
  readonly refreshPreviousDocuments: number;
};

export type DocumentsRecord = {
  readonly proofOfAddress: UploadsRecord;
  readonly proofOfDeath: UploadsRecord;
  readonly willAndProbate: UploadsRecord;
  readonly grantOfRepresentation: UploadsRecord;
  readonly proofOfBankAccount: UploadsRecord;
};

export type DocumentIds = {
  readonly proofOfAddress: string[];
  readonly proofOfDeath: string[];
  readonly willAndProbate: string[];
  readonly grantOfRepresentation: string[];
  readonly proofOfBankAccount: string[];
};

export type DocumentErrors = {
  proofOfAddress?: string;
  proofOfDeath?: string;
  willAndProbate?: string;
  grantOfRepresentation?: string;
  proofOfBankAccount?: string;
};

export type ExpressDocumentsRecord = {
  proofOfAddress: ReadonlyArray<UploadedDocument>;
  proofOfDeath: ReadonlyArray<UploadedDocument>;
  willAndProbate: ReadonlyArray<UploadedDocument>;
  grantOfRepresentation: ReadonlyArray<UploadedDocument>;
  proofOfBankAccount: ReadonlyArray<UploadedDocument>;
};

export interface DocUploadArgs {
  fileTags: Array<FileTags | InternalFileTags>;
  file: File;
}

export type CreateDocFunc = (file: DocUploadArgs) => Promise<UploadedDocument>;
